import * as THREE from "three";
import type { AssetBundle } from "@mcp/assets";

export const SKY = new THREE.Color("#9cc3e6");

export interface SharedUniforms {
  uTex: { value: THREE.DataArrayTexture };
  uFogColor: { value: THREE.Color };
  uFogNear: { value: number };
  uFogFar: { value: number };
  uCutY: { value: number };
  uTime: { value: number };
  /** 0 = flat look (no lighting), 1 = Minecraft light levels + time of day */
  uLightMix: { value: number };
  /** sky-light strength for the time of day (0.2 at night … 1 at noon) and its colour */
  uDaylight: { value: number };
  uSkyLight: { value: THREE.Color };
  /** colour of block light (torches, lanterns, glowstone…) */
  uTorch: { value: THREE.Color };
  /** never darker than this (so the map stays readable at midnight in caves) */
  uMinLight: { value: number };
}

/** GLSL helpers shared by the block and map shaders. */
const LIGHT_GLSL = /* glsl */ `
uniform float uLightMix;
uniform float uDaylight;
uniform vec3 uSkyLight;
uniform vec3 uTorch;
uniform float uMinLight;
// Minecraft-like brightness curve for light levels 0..1
float mcCurve(float l) { return l / (4.0 - 3.0 * l); }
vec3 mcLight(float sky, float block) {
  vec3 l = max(mcCurve(sky) * uDaylight * uSkyLight, mcCurve(block) * uTorch);
  return mix(vec3(1.0), max(l, vec3(uMinLight)), uLightMix);
}`;

export function createBlockTexture(tex: AssetBundle["textures"], renderer: THREE.WebGLRenderer) {
  const t = new THREE.DataArrayTexture(tex.pixels, tex.size, tex.size, tex.names.length);
  t.format = THREE.RGBAFormat;
  t.type = THREE.UnsignedByteType;
  t.magFilter = THREE.NearestFilter;
  t.minFilter = THREE.NearestMipmapLinearFilter;
  t.generateMipmaps = true;
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.anisotropy = Math.min(8, renderer.capabilities.getMaxAnisotropy());
  t.colorSpace = THREE.NoColorSpace;
  t.needsUpdate = true;
  return t;
}

const blockVert = /* glsl */ `
in vec4 uvl;
in vec4 col;
out vec2 vUv;
flat out float vLayer;
out vec4 vColor;
out float vDist;
out vec2 vLight;
void main() {
  vUv = uvl.xy / 4096.0;
  vLayer = uvl.z;
  // uvl.w = (sky*16) << 8 | (block*16)
  vLight = vec2(floor(uvl.w / 256.0), mod(uvl.w, 256.0)) / 240.0;
  vColor = col;
  vec4 mv = modelViewMatrix * vec4(position, 1.0);
  vDist = -mv.z;
  gl_Position = projectionMatrix * mv;
}`;

const blockFrag = /* glsl */ `
precision highp sampler2DArray;
uniform sampler2DArray uTex;
uniform vec3 uFogColor;
uniform float uFogNear;
uniform float uFogFar;
uniform float uAlphaTest;
in vec2 vUv;
flat in float vLayer;
in vec4 vColor;
in float vDist;
in vec2 vLight;
out vec4 outColor;
${LIGHT_GLSL}
void main() {
  vec4 t = texture(uTex, vec3(vUv, vLayer));
  if (t.a < uAlphaTest) discard;
  vec3 c = t.rgb * vColor.rgb * mcLight(vLight.x, vLight.y);
  float f = smoothstep(uFogNear, uFogFar, vDist);
  outColor = vec4(mix(c, uFogColor, f), uAlphaTest > 0.0 ? 1.0 : t.a * vColor.a);
}`;

export function createBlockMaterials(shared: SharedUniforms) {
  const solid = new THREE.ShaderMaterial({
    glslVersion: THREE.GLSL3,
    vertexShader: blockVert,
    fragmentShader: blockFrag,
    uniforms: { ...shared, uAlphaTest: { value: 0.4 } },
    side: THREE.FrontSide,
  });
  const translucent = new THREE.ShaderMaterial({
    glslVersion: THREE.GLSL3,
    vertexShader: blockVert,
    fragmentShader: blockFrag,
    uniforms: { ...shared, uAlphaTest: { value: 0.0 } },
    transparent: true,
    depthWrite: false,
    side: THREE.DoubleSide,
  });
  return { solid, translucent };
}

// ---------------- schematic previews (placed schematic buildings) ----------------

const structFrag = /* glsl */ `
precision highp sampler2DArray;
uniform sampler2DArray uTex;
uniform vec3 uFogColor;
uniform float uFogNear;
uniform float uFogFar;
uniform float uAlphaTest;
uniform vec3 uTint;
uniform float uTintMix;
uniform float uOpacity;
in vec2 vUv;
flat in float vLayer;
in vec4 vColor;
in float vDist;
in vec2 vLight;
out vec4 outColor;
${LIGHT_GLSL}
void main() {
  vec4 t = texture(uTex, vec3(vUv, vLayer));
  if (t.a < uAlphaTest) discard;
  vec3 c = t.rgb * vColor.rgb * mcLight(vLight.x, vLight.y);
  c = mix(c, uTint, uTintMix);
  float f = smoothstep(uFogNear, uFogFar, vDist);
  outColor = vec4(mix(c, uFogColor, f), (uAlphaTest > 0.0 ? 1.0 : t.a * vColor.a) * uOpacity);
}`;

export interface StructureMaterials {
  /** depth only: so the see-through colour pass only draws the front-most faces (no x-ray mess inside) */
  depth: THREE.ShaderMaterial;
  solid: THREE.ShaderMaterial;
  translucent: THREE.ShaderMaterial;
  /** 1 = drawn like real blocks, < 1 = see-through (planned / idea) */
  opacity: number;
}

/**
 * Block materials for a placed schematic: real textures, lightly tinted with the object's colour and
 * see-through while it's only planned. Shares the texture array + fog/light uniforms with the world.
 */
export function createStructureMaterials(shared: SharedUniforms, tint: THREE.Color, tintMix: number, opacity: number): StructureMaterials {
  const own = () => ({ uTint: { value: tint }, uTintMix: { value: tintMix }, uOpacity: { value: opacity } });
  const see = opacity < 0.999;
  const depth = new THREE.ShaderMaterial({
    glslVersion: THREE.GLSL3, vertexShader: blockVert, fragmentShader: structFrag,
    uniforms: { ...shared, ...own(), uAlphaTest: { value: 0.4 } },
    colorWrite: false,
  });
  const solid = new THREE.ShaderMaterial({
    glslVersion: THREE.GLSL3, vertexShader: blockVert, fragmentShader: structFrag,
    uniforms: { ...shared, ...own(), uAlphaTest: { value: 0.4 } },
    side: THREE.FrontSide,
    transparent: see,
    depthWrite: !see,
    depthFunc: THREE.LessEqualDepth,
  });
  const translucent = new THREE.ShaderMaterial({
    glslVersion: THREE.GLSL3, vertexShader: blockVert, fragmentShader: structFrag,
    uniforms: { ...shared, ...own(), uAlphaTest: { value: 0.0 } },
    transparent: true,
    depthWrite: false,
    side: THREE.DoubleSide,
  });
  return { depth, solid, translucent, opacity };
}

// ---------------- LOD (region map) ----------------

const lodVert = /* glsl */ `
precision highp isampler2D;
uniform isampler2D uHeight;
uniform vec2 uOrigin;
uniform float uCutY;
out vec2 vBlock;
out float vDist;
void main() {
  vec2 local = position.xz;                 // 0..512
  ivec2 b = clamp(ivec2(floor(local)), ivec2(0), ivec2(511));
  // use the max of the 2x2 block footprint so the coarse grid doesn't dip below surfaces
  int h = texelFetch(uHeight, b, 0).r;
  vBlock = local;
  vec4 world = vec4(uOrigin.x + local.x, min(float(h), uCutY + 1.0) - 0.02, uOrigin.y + local.y, 1.0);
  vec4 mv = viewMatrix * world;
  vDist = -mv.z;
  gl_Position = projectionMatrix * mv;
}`;

const lodFrag = /* glsl */ `
precision highp isampler2D;
uniform sampler2D uColor;
uniform isampler2D uHeight;
uniform sampler2D uMask;
uniform vec3 uFogColor;
uniform float uFogNear;
uniform float uFogFar;
uniform float uCutY;
in vec2 vBlock;
in float vDist;
out vec4 outColor;
${LIGHT_GLSL}
void main() {
  ivec2 b = clamp(ivec2(floor(vBlock)), ivec2(0), ivec2(511));
  if (texelFetch(uMask, b / 16, 0).r > 0.5) discard;   // detailed chunk mesh is shown here
  vec4 c = texture(uColor, vBlock / 512.0);
  if (c.a < 0.5) discard;
  int h = texelFetch(uHeight, b, 0).r;
  int hn = texelFetch(uHeight, clamp(b - ivec2(0, 1), ivec2(0), ivec2(511)), 0).r;
  int hw = texelFetch(uHeight, clamp(b - ivec2(1, 0), ivec2(0), ivec2(511)), 0).r;
  float d = float(h - hn) * 0.6 + float(h - hw) * 0.4;
  float shade = c.a < 0.999 ? 1.0 : clamp(1.0 + d * 0.06, 0.72, 1.12);
  vec3 col = c.rgb * shade;
  if (float(h) > uCutY + 1.0) col = vec3(0.42, 0.40, 0.38);  // sliced: unknown interior, show as cut rock
  else col *= mcLight(1.0, 0.0);                                 // map tiles are the sky-lit surface
  float f = smoothstep(uFogNear, uFogFar, vDist);
  outColor = vec4(mix(col, uFogColor, f), 1.0);
}`;

let lodGeometry: THREE.BufferGeometry | null = null;
function getLodGeometry() {
  if (!lodGeometry) {
    const g = new THREE.PlaneGeometry(512, 512, 128, 128);
    g.rotateX(-Math.PI / 2);
    g.translate(256, 0, 256);
    lodGeometry = g;
  }
  return lodGeometry;
}

export interface LodTile {
  mesh: THREE.Mesh;
  mask: THREE.DataTexture;
  maskData: Uint8Array;
  color: THREE.DataTexture;
  height: THREE.DataTexture;
}

export function createLodTile(rx: number, rz: number, color: Uint8Array, height: Int16Array, shared: SharedUniforms): LodTile {
  const ct = new THREE.DataTexture(color, 512, 512, THREE.RGBAFormat, THREE.UnsignedByteType);
  ct.magFilter = THREE.NearestFilter;
  ct.minFilter = THREE.LinearMipmapLinearFilter;
  ct.generateMipmaps = true;
  ct.flipY = false;
  ct.needsUpdate = true;
  // premultiply-ish: fill alpha-0 texels are discarded anyway
  const ht = new THREE.DataTexture(new Int32Array(height), 512, 512, THREE.RedIntegerFormat, THREE.IntType);
  ht.internalFormat = "R32I";
  ht.magFilter = ht.minFilter = THREE.NearestFilter;
  ht.needsUpdate = true;
  const maskData = new Uint8Array(32 * 32);
  const mt = new THREE.DataTexture(maskData, 32, 32, THREE.RedFormat, THREE.UnsignedByteType);
  mt.magFilter = mt.minFilter = THREE.NearestFilter;
  mt.needsUpdate = true;
  const mat = new THREE.ShaderMaterial({
    glslVersion: THREE.GLSL3,
    vertexShader: lodVert,
    fragmentShader: lodFrag,
    uniforms: {
      uColor: { value: ct },
      uHeight: { value: ht },
      uMask: { value: mt },
      uOrigin: { value: new THREE.Vector2(rx * 512, rz * 512) },
      uFogColor: shared.uFogColor,
      uFogNear: shared.uFogNear,
      uFogFar: shared.uFogFar,
      uCutY: shared.uCutY,
      uLightMix: shared.uLightMix,
      uDaylight: shared.uDaylight,
      uSkyLight: shared.uSkyLight,
      uTorch: shared.uTorch,
      uMinLight: shared.uMinLight,
    },
  });
  const mesh = new THREE.Mesh(getLodGeometry(), mat);
  mesh.frustumCulled = false; // vertex shader displaces; bounding volume is wrong
  mesh.matrixAutoUpdate = false;
  mesh.renderOrder = -1;
  return { mesh, mask: mt, maskData, color: ct, height: ht };
}

// ---------- plan objects drawn with a block texture (paths, areas, building walls) ----------

const planTexVert = /* glsl */ `
// "uv" is declared by three.js for ShaderMaterial
out vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}`;

const planTexFrag = /* glsl */ `
precision highp sampler2DArray;
uniform sampler2DArray uTex;
uniform float uLayer;
uniform float uUnder;   // ground under see-through textures (crops on farmland), -1 = none
uniform vec3 uTint;
uniform vec3 uColor;
uniform float uOpacity;
in vec2 vUv;
out vec4 outColor;
void main() {
  // repeat the 16×16 texture once per block; explicit gradients avoid seams at the wrap
  vec4 t = textureGrad(uTex, vec3(fract(vUv), uLayer), dFdx(vUv), dFdy(vUv));
  if (uUnder >= 0.0 && t.a < 0.5) t = vec4(textureGrad(uTex, vec3(fract(vUv), uUnder), dFdx(vUv), dFdy(vUv)).rgb, 1.0);
  if (t.a < 0.1) discard;
  vec3 c = mix(t.rgb * uTint, uColor, 0.12);
  outColor = vec4(c, uOpacity * max(t.a, 0.35));
}`;

/** Material showing one layer of the block texture array, lightly tinted with the object colour. */
export function createPlanTextureMaterial(uTex: SharedUniforms["uTex"], layer: number, tint: THREE.Color, color: THREE.Color, opacity: number, under = -1) {
  return new THREE.ShaderMaterial({
    glslVersion: THREE.GLSL3,
    vertexShader: planTexVert,
    fragmentShader: planTexFrag,
    uniforms: {
      uTex,
      uLayer: { value: layer },
      uUnder: { value: under },
      uTint: { value: tint },
      uColor: { value: color },
      uOpacity: { value: opacity },
    },
    transparent: true,
    depthWrite: false,
    side: THREE.DoubleSide,
    polygonOffset: true,
    polygonOffsetFactor: -2,
    polygonOffsetUnits: -2,
  });
}

// ---------- sky dome (only with lighting on) ----------

const skyVert = /* glsl */ `
out vec3 vDir;
void main() {
  vDir = normalize(position);
  vec4 p = projectionMatrix * viewMatrix * vec4(position + cameraPosition, 1.0);
  gl_Position = vec4(p.xy, p.w * 0.99999, p.w); // always just inside the far plane
}`;

const skyFrag = /* glsl */ `
uniform vec3 uZenith;
uniform vec3 uHorizon;
uniform vec3 uSunDir;
uniform float uStars;
in vec3 vDir;
out vec4 outColor;
float hash(vec3 p) { p = fract(p * 0.3183099 + 0.1); p *= 17.0; return fract(p.x * p.y * p.z * (p.x + p.y + p.z)); }
void main() {
  vec3 d = normalize(vDir);
  float h = d.y;
  vec3 col = mix(uHorizon, uZenith, smoothstep(0.0, 0.55, h));
  if (h < 0.0) col = mix(uHorizon, uHorizon * 0.55, smoothstep(0.0, -0.3, h));
  float s = dot(d, uSunDir);
  // sun: square-ish bright disc + soft glow, moon on the opposite side
  col += vec3(1.0, 0.9, 0.7) * pow(max(s, 0.0), 180.0) * 0.6 + vec3(1.0, 0.95, 0.8) * smoothstep(0.9975, 0.9985, s);
  col += vec3(0.85, 0.9, 1.0) * smoothstep(0.9985, 0.9992, -s) * 0.9;
  // stars
  if (uStars > 0.0 && h > 0.0) {
    vec3 g = floor(d * 380.0);
    float st = step(0.9975, hash(g));
    col += vec3(st) * uStars * smoothstep(0.0, 0.25, h);
  }
  outColor = vec4(col, 1.0);
}`;

export function createSky() {
  const uniforms = {
    uZenith: { value: new THREE.Color() },
    uHorizon: { value: new THREE.Color() },
    uSunDir: { value: new THREE.Vector3(0, 1, 0) },
    uStars: { value: 0 },
  };
  const mat = new THREE.ShaderMaterial({
    glslVersion: THREE.GLSL3,
    vertexShader: skyVert,
    fragmentShader: skyFrag,
    uniforms,
    side: THREE.BackSide,
    depthWrite: false,
    depthTest: false,
  });
  const mesh = new THREE.Mesh(new THREE.SphereGeometry(10, 32, 16), mat);
  mesh.frustumCulled = false;
  mesh.renderOrder = -10;
  mesh.visible = false;
  return { mesh, uniforms };
}

// ---------- time of day ----------

/** Minecraft ticks (0 = sunrise, 6000 = noon, 12000 = sunset, 18000 = midnight) → lighting parameters. */
export function timeOfDay(ticks: number) {
  const t = (((ticks % 24000) + 24000) % 24000) / 24000;
  const a = t * Math.PI * 2;
  const sunH = Math.sin(a);
  const day = Math.min(1, Math.max(0, sunH * 2 + 0.5)); // 0 night … 1 day
  const dusk = Math.max(0, 1 - Math.abs(sunH) * 4) * (sunH > -0.25 ? 1 : 0); // around sunrise/sunset
  const lerp = (x: THREE.Color, y: THREE.Color, k: number) => x.clone().lerp(y, k);
  const zenith = lerp(new THREE.Color(0.012, 0.02, 0.06), new THREE.Color(0.36, 0.58, 0.95), day);
  const horizon = lerp(new THREE.Color(0.04, 0.07, 0.15), new THREE.Color(0.7, 0.82, 0.98), day).lerp(new THREE.Color(1.0, 0.55, 0.3), dusk * 0.75);
  const skyLight = lerp(new THREE.Color(0.55, 0.68, 1.0), new THREE.Color(1, 1, 1), day).lerp(new THREE.Color(1.0, 0.82, 0.68), dusk * 0.5);
  return {
    // Minecraft goes down to ~0.2; a bit brighter here so plans stay readable at night
    daylight: 0.3 + 0.7 * day,
    skyLight,
    zenith,
    horizon,
    sunDir: new THREE.Vector3(Math.cos(a), sunH, 0.18).normalize(),
    stars: Math.max(0, 1 - day * 1.6),
  };
}

/** Minecraft ticks → "14:30" (tick 0 = 06:00). */
export function mcClock(ticks: number) {
  const h = ((ticks / 1000 + 6) % 24 + 24) % 24;
  return `${String(Math.floor(h)).padStart(2, "0")}:${String(Math.floor((h % 1) * 60)).padStart(2, "0")}`;
}
